/**
 * APIキー管理コンポーネント
 *
 * 顧客詳細画面に埋め込まれるAPIキー管理UI。
 * キー一覧表示・コピー・有効無効切替・新規発行を提供。
 */

import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Paper,
  Button,
  Text,
  Group,
  Loader,
  Title,
  Badge,
  ActionIcon,
  CopyButton,
  Tooltip,
  Code,
  Switch,
} from "@mantine/core";
import { showSuccess, showError } from "../../utils/notifications";
import {
  ApiKeyItem,
  fetchApiKeys,
  createApiKey,
  toggleApiKey,
} from "../../services/deliveryService";
import { Plus, Copy, Check, Key } from "lucide-react";

interface ApiKeyManagerProps {
  customerId: string;
}

export function ApiKeyManager({ customerId }: ApiKeyManagerProps) {
  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // データを読み込み
  const loadData = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const keys = await fetchApiKeys(customerId);
      setApiKeys(keys);
    } catch (error) {
      console.error(error);
      showError("APIキーの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // APIキーを新規発行
  const handleCreate = async () => {
    setCreating(true);
    try {
      const key = await createApiKey(customerId, "手動発行");
      showSuccess(`APIキーを発行しました: ${key}`);
      await loadData();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "APIキーの発行に失敗しました"
      );
    } finally {
      setCreating(false);
    }
  };

  // 有効/無効切替
  const handleToggle = async (keyId: string, isActive: boolean) => {
    try {
      await toggleApiKey(customerId, keyId, isActive);
      showSuccess(`APIキーを${isActive ? "有効" : "無効"}にしました`);
      await loadData();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "APIキーの更新に失敗しました"
      );
    }
  };

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group justify="space-between" mb="md" align="center">
        <Group gap="xs">
          <Key size={18} />
          <Title order={5}>APIキー管理</Title>
        </Group>
        <Button
          size="xs"
          leftSection={<Plus size={14} />}
          onClick={handleCreate}
          loading={creating}
          variant="light"
        >
          新規発行
        </Button>
      </Group>

      {loading && apiKeys.length === 0 ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : apiKeys.length === 0 ? (
        <Text c="dimmed" ta="center" py="md" size="sm">
          APIキーが発行されていません
        </Text>
      ) : (
        <Stack gap="xs">
          {apiKeys.map((apiKey) => (
            <Paper
              key={apiKey.id}
              p="sm"
              withBorder
              style={{
                borderColor: apiKey.isActive
                  ? "var(--mantine-color-green-3)"
                  : "var(--mantine-color-gray-3)",
                opacity: apiKey.isActive ? 1 : 0.6,
                transition: "all 0.2s ease",
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <Code
                    style={{
                      fontSize: "0.85rem",
                      fontFamily:
                        "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {apiKey.key}
                  </Code>

                  <CopyButton value={apiKey.key} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "コピーしました" : "コピー"}
                        withArrow
                      >
                        <ActionIcon
                          color={copied ? "teal" : "gray"}
                          variant="subtle"
                          onClick={copy}
                          size="sm"
                        >
                          {copied ? <Check size={14} /> : <Copy size={14} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>

                  <Badge
                    size="xs"
                    variant="light"
                    color={apiKey.isActive ? "green" : "gray"}
                  >
                    {apiKey.isActive ? "有効" : "無効"}
                  </Badge>

                  <Text size="xs" c="dimmed">
                    {apiKey.label}
                  </Text>
                </Group>

                <Switch
                  checked={apiKey.isActive}
                  onChange={(event) =>
                    handleToggle(apiKey.id, event.currentTarget.checked)
                  }
                  size="sm"
                  color="green"
                />
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
